/**
 * AdminPage - Game administration dashboard.
 *
 * M1: Lists all games with status, version, AI Score, Retention Score.
 * Supports status transitions via the state machine API and shows
 * basic per-game analytics.
 *
 * Route: /admin/games
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { gamesApi, analyticsApi } from '../lib/apiClient';
import { GameStatus, getNextStatuses } from '@adgf/shared';

/** Minimal game shape used by the admin dashboard. */
interface AdminGame {
  id: string;
  slug: string;
  title: string;
  description?: string;
  version: string;
  status: string;
  difficulty?: string;
  ai_score: number;
  retention_score: number;
  created_at: string;
  published_at?: string | null;
}

interface GameAnalytics {
  plays?: number;
  unique_players?: number;
  average_score?: number;
  completion_rate?: number;
}

/** Badge colors per game status. */
const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  DRAFT: { bg: '#4b5563', fg: '#ffffff' },
  BUILDING: { bg: '#2563eb', fg: '#ffffff' },
  TESTING: { bg: '#ca8a04', fg: '#ffffff' },
  APPROVED: { bg: '#16a34a', fg: '#ffffff' },
  PUBLISHED: { bg: '#059669', fg: '#ffffff' },
  PAUSED: { bg: '#ea580c', fg: '#ffffff' },
  KILLED: { bg: '#dc2626', fg: '#ffffff' },
};

/** Friendly button label per target status. */
const TRANSITION_LABELS: Record<string, string> = {
  BUILDING: 'Start Building',
  TESTING: 'Send to Testing',
  APPROVED: 'Approve',
  PUBLISHED: 'Publish',
  PAUSED: 'Pause',
  KILLED: 'Kill',
  DRAFT: 'Reopen Draft',
};

/** data-testid per target status (PUBLISHED/PAUSED have dedicated E2E ids). */
function transitionTestId(target: string): string {
  if (target === 'PUBLISHED') return 'admin-publish-btn';
  if (target === 'PAUSED') return 'admin-pause-btn';
  return `admin-transition-btn-${target.toLowerCase()}`;
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString().slice(0, 10);
}

export default function AdminPage() {
  const [games, setGames] = useState<AdminGame[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expandedStatsId, setExpandedStatsId] = useState<string | null>(null);
  const [statsLoadingId, setStatsLoadingId] = useState<string | null>(null);
  const [stats, setStats] = useState<Record<string, GameAnalytics>>({});

  const loadGames = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await gamesApi.list();
      setGames((res.games ?? []) as AdminGame[]);
    } catch (err) {
      // The API may not be running (e.g. during unit tests). Render the
      // page structure anyway instead of crashing.
      setLoadError(err instanceof Error ? err.message : 'Failed to load games');
      setGames([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadGames();
  }, [loadGames]);

  const handleTransition = async (game: AdminGame, target: string) => {
    setActionMessage(null);
    setActionError(null);
    try {
      // APPROVED and PUBLISHED are human-only transitions (M2 Approval Gate).
      // A click in this admin UI constitutes explicit human approval.
      const isHumanOnly = target === 'APPROVED' || target === 'PUBLISHED';
      await gamesApi.transition(game.id, target, {
        humanApproved: isHumanOnly,
        approverId: isHumanOnly ? 'admin-user' : undefined,
      });
      setActionMessage(`"${game.title}" transitioned to ${target}.`);
      await loadGames();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : `Failed to transition to ${target}`,
      );
    }
  };

  const handleViewStats = async (game: AdminGame) => {
    if (expandedStatsId === game.id) {
      setExpandedStatsId(null);
      return;
    }
    setExpandedStatsId(game.id);
    setStatsLoadingId(game.id);
    try {
      const res = await analyticsApi.getGameAnalytics(game.id);
      setStats((prev) => ({ ...prev, [game.id]: res as GameAnalytics }));
    } catch {
      setStats((prev) => ({ ...prev, [game.id]: {} }));
    } finally {
      setStatsLoadingId((cur) => (cur === game.id ? null : cur));
    }
  };

  return (
    <div style={{ padding: '24px 0' }} data-testid="admin-page">
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Game Admin</h2>
        <Link to="/" style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>
          &larr; Back to Games
        </Link>
      </div>

      {actionMessage && (
        <div
          role="status"
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            borderRadius: 'var(--radius)',
            background: 'rgba(5, 150, 105, 0.12)',
            color: '#059669',
            fontSize: '0.9rem',
          }}
        >
          {actionMessage}
        </div>
      )}
      {actionError && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            borderRadius: 'var(--radius)',
            background: 'rgba(220, 38, 38, 0.12)',
            color: '#dc2626',
            fontSize: '0.9rem',
          }}
        >
          {actionError}
        </div>
      )}

      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Loading games...</p>}

      {loadError && (
        <div
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            borderRadius: 'var(--radius)',
            background: 'rgba(220, 38, 38, 0.12)',
            color: '#dc2626',
            fontSize: '0.9rem',
          }}
        >
          API unavailable: {loadError}
        </div>
      )}

      <div data-testid="admin-games-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!loading && games.length === 0 && !loadError && (
          <p style={{ color: 'var(--color-text-muted)' }}>No games found.</p>
        )}

        {games.map((game) => {
          const status = game.status as GameStatus;
          const nextStatuses = getNextStatuses(status);
          const badge = STATUS_COLORS[status] ?? { bg: '#6b7280', fg: '#ffffff' };
          const isExpanded = expandedStatsId === game.id;
          const gameStats = stats[game.id];

          return (
            <div
              key={game.id}
              data-testid="admin-game-row"
              style={{
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                padding: 16,
                background: 'var(--color-bg-secondary)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 16,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong>{game.title}</strong>
                    <span
                      data-testid={`admin-status-badge-${game.id}`}
                      style={{
                        background: badge.bg,
                        color: badge.fg,
                        borderRadius: 999,
                        padding: '2px 10px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      {status}
                    </span>
                  </div>
                  <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
                    {game.slug}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 16, fontSize: '0.85rem', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: 'var(--color-text-muted)' }}>Version</div>
                    <div>v{game.version}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--color-text-muted)' }}>AI Score</div>
                    <div>{game.ai_score}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--color-text-muted)' }}>Retention</div>
                    <div>{game.retention_score}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--color-text-muted)' }}>Created</div>
                    <div>{formatDate(game.created_at)}</div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: 12,
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                {nextStatuses.length === 0 && (
                  <span style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
                    No further transitions (terminal state).
                  </span>
                )}
                {nextStatuses.map((target) => (
                  <button
                    key={target}
                    data-testid={transitionTestId(target)}
                    onClick={() => void handleTransition(game, target)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 'var(--radius)',
                      border: '1px solid var(--color-border)',
                      background:
                        target === 'PUBLISHED'
                          ? 'var(--color-primary)'
                          : 'transparent',
                      color:
                        target === 'PUBLISHED'
                          ? '#ffffff'
                          : 'var(--color-text)',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                    }}
                  >
                    {TRANSITION_LABELS[target] ?? target}
                  </button>
                ))}
                <button
                  data-testid="admin-stats-btn"
                  onClick={() => void handleViewStats(game)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 'var(--radius)',
                    border: '1px solid var(--color-border)',
                    background: 'transparent',
                    color: 'var(--color-text)',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                  }}
                >
                  {isExpanded ? 'Hide Stats' : 'View Stats'}
                </button>
              </div>

              {isExpanded && (
                <div
                  data-testid="admin-game-stats"
                  style={{
                    marginTop: 12,
                    padding: 12,
                    border: '1px dashed var(--color-border)',
                    borderRadius: 'var(--radius)',
                    fontSize: '0.85rem',
                  }}
                >
                  {statsLoadingId === game.id ? (
                    <span style={{ color: 'var(--color-text-muted)' }}>Loading stats...</span>
                  ) : (
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                      <span>Plays: {gameStats?.plays ?? '—'}</span>
                      <span>Unique players: {gameStats?.unique_players ?? '—'}</span>
                      <span>Average score: {gameStats?.average_score ?? '—'}</span>
                      <span>Completion rate: {gameStats?.completion_rate ?? '—'}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
